// Share authoring — admin only.
//
// Creating a link publishes career history to a URL anyone holding it can open,
// so these are gated on is_admin server-side, not merely on being signed in.
import { apiFetch } from '../utils/apiFetch';
import { API_URL } from '../config';

const BASE = `${API_URL}/api/admin/shares`;
const jsonHeaders = { 'Content-Type': 'application/json' };

async function asJson(res) {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Share admin ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

/** The postings a fit tab can be generated from (hardcoded catalog for now). */
export async function listPostings() {
  return asJson(await apiFetch(`${BASE}/postings`));
}

/** Every draft, what it still needs written, and any links minted from it. */
export async function listDrafts() {
  return asJson(await apiFetch(BASE));
}

export async function createDraft(payload) {
  return asJson(await apiFetch(`${BASE}/drafts`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify(payload),
  }));
}

/** Kick off generation. Returns {task_id} — poll the task for progress. */
export async function generateTab(contentId, postingId) {
  return asJson(await apiFetch(`${BASE}/drafts/${encodeURIComponent(contentId)}/generate`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify({ posting_id: postingId }),
  }));
}

/** The page payload exactly as a recipient would receive it. No token exists. */
export async function previewDraft(contentId) {
  return asJson(await apiFetch(`${BASE}/drafts/${encodeURIComponent(contentId)}/preview`));
}

export async function updateItem(contentId, tabId, itemId, fields) {
  return asJson(await apiFetch(
    `${BASE}/drafts/${encodeURIComponent(contentId)}/tabs/${encodeURIComponent(tabId)}/items/${encodeURIComponent(itemId)}`,
    { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(fields) },
  ));
}

export async function updateOpening(contentId, tabId, opening) {
  return asJson(await apiFetch(
    `${BASE}/drafts/${encodeURIComponent(contentId)}/tabs/${encodeURIComponent(tabId)}/opening`,
    { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ opening }) },
  ));
}

/**
 * Create the link. Returns {ok:false, unwritten:[...]} while statements are
 * still blank — that is the draft not being finished, not an error.
 * The token is stored on the Share, so it remains retrievable from listDrafts.
 */
export async function mintShare(contentId, payload) {
  return asJson(await apiFetch(`${BASE}/drafts/${encodeURIComponent(contentId)}/mint`, {
    method: 'POST', headers: jsonHeaders, body: JSON.stringify(payload),
  }));
}

/** Issue a new link for a share; the old one stops working. */
export async function rotateShare(shareId) {
  return asJson(await apiFetch(`${BASE}/${encodeURIComponent(shareId)}/rotate`, {
    method: 'POST',
  }));
}

export async function revokeShare(shareId) {
  return asJson(await apiFetch(`${BASE}/${encodeURIComponent(shareId)}/revoke`, {
    method: 'POST',
  }));
}

/** Poll a background task (generation) until it settles. */
export async function getTask(taskId) {
  return asJson(await apiFetch(`${API_URL}/api/tasks/${encodeURIComponent(taskId)}`));
}
